import { memo, useMemo } from "react";
import { Check, CheckSquare, Search, Square } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

import { getOptionKey } from "../helpers";
import type { FilterOption } from "../types";

interface OptionsListProps {
  options: (string | number | FilterOption)[];
  group: string;
  searches: Record<string, string>;
  selected: Record<string, any[]>;
  onToggle: (group: string, option: any) => void;
  onBulkToggle: (group: string, optionsToToggle: any[], selectAll: boolean) => void;
}

/* Lista de opções multivalor com busca, ordenação e ação "selecionar/limpar todos". */
export const OptionsList = memo(function OptionsList({
  options = [],
  group,
  searches,
  selected,
  onToggle,
  onBulkToggle,
}: OptionsListProps) {
  const filtered = useMemo(() => {
    const term = (searches[group] || "").toLowerCase();
    if (!term) return options;

    return options.filter((option) => {
      const label =
        typeof option === "object" && option !== null ? String(option.nome) : String(option);
      return label.toLowerCase().includes(term);
    });
  }, [options, searches, group]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const valA = typeof a === "object" ? a.nome : a;
      const valB = typeof b === "object" ? b.nome : b;
      const numA = Number(valA);
      const numB = Number(valB);

      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return String(valA).localeCompare(String(valB), undefined, { numeric: true });
    });
  }, [filtered]);

  const isAllSelected =
    sorted.length > 0 &&
    sorted.every((option) => {
      const isObject = typeof option === "object" && option !== null;
      const optionValue = isObject ? option.id : option;
      return selected[group]?.some((item) =>
        isObject ? item.id === optionValue : item === optionValue,
      );
    });

  if (!options.length) {
    return (
      <div className="rounded-md bg-muted/30 px-2 py-8 text-center text-xs italic text-muted-foreground">
        Nenhuma opção disponível
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between px-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {sorted.length} opções
        </span>
        {sorted.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-primary hover:text-primary/80"
            onClick={() => onBulkToggle(group, sorted, !isAllSelected)}
          >
            {isAllSelected ? (
              <Square className="mr-1 h-3 w-3" />
            ) : (
              <CheckSquare className="mr-1 h-3 w-3" />
            )}
            {isAllSelected ? "Limpar" : "Todos"}
          </Button>
        )}
      </div>

      <ScrollArea className="h-[200px] w-full pr-3">
        <div className="flex flex-wrap gap-2 pb-2">
          {sorted.map((option, idx) => {
            const key = getOptionKey(option, idx);
            const isObject = typeof option === "object" && option !== null;
            const optionValue = isObject ? option.id : option;
            const optionLabel = isObject ? option.nome : String(option);
            const checked = selected[group]?.some((item) =>
              isObject ? item.id === optionValue : item === optionValue,
            );

            // Pill com alvo de clique único: evita o double-toggle do par checkbox+label htmlFor.
            return (
              <button
                type="button"
                key={key}
                onClick={() => onToggle(group, option)}
                aria-pressed={checked}
                className={cn(
                  "inline-flex select-none items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                  checked
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-transparent bg-secondary/40 text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )}
              >
                {checked && <Check className="h-3 w-3 shrink-0" />}
                <span className="leading-tight">{optionLabel}</span>
              </button>
            );
          })}

          {filtered.length === 0 && (
            <div className="flex h-full w-full flex-col items-center justify-center py-4 text-center">
              <Search className="mb-2 h-8 w-8 text-muted-foreground/30" />
              <span className="text-xs text-muted-foreground">
                Nenhum resultado para "{searches[group]}"
              </span>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
});
